import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { AlertTriangle, Check, ChevronDown, CornerDownRight, RefreshCw, Send, Wrench } from 'lucide-react'
import { LoadingButton } from '@/components/loading-button'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { Button } from '@/components/ui/button'
import { cn, formatMoney } from '@/lib/utils'
import { useQuoteBuilderApi } from '../api/client'
import { quoteQueryOptions, workflowQueryOptions } from '../api/queries'
import type { QuoteLine } from '../api/types'
import { AiData } from '../ui/ai-data'
import { PageState, SurfaceCard } from '../ui/page'
import { QuoteLineIdentity } from '../ui/product'
import { Money } from '../ui/values'
import type { WorkflowTabProps } from '../workflow/types'
import {
  applyServices,
  draftEstimate,
  mergeDrafts,
  ServicesConflictError,
  servicesError,
  servicesQueryOptions,
  type InstallationEstimate,
  type InstallationEstimateInput,
  type InstallationPreview,
} from './services-api'
import {
  allocateServiceNet,
  allocateServiceSell,
  draftIsInvalid,
  round,
  serviceIntelligence,
  servicePricing,
} from './services-pricing'

const INSTALL_CHOICES = [
  { value: 'install', label: 'Install' },
  { value: 'no_install', label: 'No install' },
] as const

function ServiceValue({
  label,
  value,
  display,
  kind = 'number',
  onChange,
}: {
  label: string
  value: string
  display: string
  kind?: 'number' | 'text' | 'textarea'
  onChange: (value: string) => void
}) {
  return (
    <div className="min-w-0">
      <span className="block text-xs text-muted-foreground">{label}</span>
      <POInlineEditableField
        allowWrap={kind === 'textarea'}
        ariaLabel={label}
        buttonClassName="-ml-2 min-w-0 w-full justify-start font-medium tabular-nums"
        displayValue={display}
        kind={kind}
        onConfirm={onChange}
        title={`Edit ${label.toLowerCase()}`}
        value={value}
      />
    </div>
  )
}

export function ServiceRow({
  estimate,
  draft,
  line,
  serviceLine,
  number,
  child,
  onChange,
}: {
  estimate: InstallationEstimate
  draft: InstallationEstimateInput
  line?: QuoteLine
  serviceLine?: QuoteLine
  number: string
  child: boolean
  onChange: (changes: Partial<InstallationEstimateInput>) => void
}) {
  const pricing = servicePricing(draft, serviceLine)
  const net = Number(draft.net_override ?? round(Number(draft.crew_hours || 0) * Number(draft.hourly_rate || 0)))
  const markup = draft.markup_percent ?? (net ? String((pricing.unitSell / net - 1) * 100) : '0')
  const margin = pricing.unitSell ? (1 - net / pricing.unitSell) * 100 : 0
  const equipment = estimate.equipment
  return (
    <article
      aria-label={`${equipment.model} installation`}
      className={cn('border-t first:border-t-0', child && 'bg-muted/30')}
    >
      <div className={cn('flex items-start gap-3 px-4 pt-3', child && 'pl-8')}>
        <span className="w-7 shrink-0 pt-1 text-xs font-medium tabular-nums text-muted-foreground">{number}</span>
        {line ? (
          <QuoteLineIdentity className="min-w-0 flex-1" compact description={equipment.taxonomy_label} line={line} />
        ) : (
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{equipment.model}</p>
            <p className="truncate text-xs text-muted-foreground">{equipment.manufacturer}</p>
          </div>
        )}
        <span className="shrink-0 pt-1 text-xs tabular-nums text-muted-foreground">
          Qty {Number(equipment.quantity)}
        </span>
      </div>
      <div
        className={cn(
          'grid gap-3 px-4 py-3 xl:grid-cols-[minmax(9rem,1fr)_minmax(25rem,3fr)_minmax(7rem,0.8fr)] xl:items-center xl:gap-5',
          child ? 'pl-8 sm:pl-[4.5rem]' : 'sm:pl-[3.25rem]',
        )}
      >
        <div className="flex items-start gap-2">
          <CornerDownRight aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <p className="text-sm font-medium">{estimate.service_name}</p>
            <label className="mt-1 flex min-h-8 w-fit cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <input
                aria-label={`Include installation for ${equipment.model}`}
                checked={draft.included}
                className="size-4 rounded border-border accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onChange={(event) => onChange({ included: event.target.checked })}
                type="checkbox"
              />
              {draft.included ? 'Included' : 'Not required'}
            </label>
          </div>
        </div>
        <AiData className="grid gap-3 px-3 pb-2 pt-3" label="Installation labor estimate">
          <div className="grid grid-cols-3 gap-3">
            <ServiceValue
              display={`${draft.crew_hours || '-'} hrs`}
              label="Crew hours / unit"
              onChange={(crew_hours) => onChange({ crew_hours })}
              value={draft.crew_hours}
            />
            <ServiceValue
              display={formatMoney(Number(draft.hourly_rate || 0))}
              label="Hourly rate"
              onChange={(hourly_rate) => onChange({ hourly_rate })}
              value={draft.hourly_rate}
            />
            <ServiceValue
              display={draft.quantity || '-'}
              label="Service qty"
              onChange={(quantity) => onChange({ quantity })}
              value={draft.quantity}
            />
          </div>
          <div className="grid grid-cols-3 items-start gap-3 border-t border-emerald-200/70 pt-2 dark:border-emerald-900">
            <div className="min-w-0">
              <ServiceValue
                display={formatMoney(net)}
                label="Net / unit"
                onChange={(net_override) => onChange({ net_override })}
                value={String(net)}
              />
              <ServiceValue
                display={`${Number(markup).toFixed(2)}%`}
                kind="text"
                label="Markup"
                onChange={(markup_percent) => onChange({ markup_percent })}
                value={String(round(Number(markup)))}
              />
              {draft.net_override != null ? (
                <button
                  className="min-h-8 text-xs font-medium text-primary underline-offset-4 hover:underline"
                  onClick={() => onChange({ net_override: null })}
                  type="button"
                >
                  Use labor Net
                </button>
              ) : null}
            </div>
            <div className="min-w-0">
              <ServiceValue
                display={formatMoney(pricing.unitSell)}
                label="Sell / unit"
                onChange={(sell) =>
                  onChange({
                    markup_percent: net ? String((Number(sell) / net - 1) * 100) : null,
                  })
                }
                value={String(pricing.unitSell)}
              />
              <ServiceValue
                display={`${margin.toFixed(2)}%`}
                kind="text"
                label="Margin"
                onChange={(value) =>
                  onChange({
                    markup_percent:
                      Number(value) >= 100 ? 'invalid' : String((Number(value) / (100 - Number(value))) * 100),
                  })
                }
                value={String(round(margin))}
              />
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Total sell</p>
              <p className="mt-1 text-sm font-semibold">
                <Money value={pricing.sell} />
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                <Money className="font-normal" value={pricing.profit} /> profit
              </p>
              <p className="text-xs text-muted-foreground">
                <Money className="font-normal" value={pricing.cost} /> Net
              </p>
            </div>
          </div>
        </AiData>
        <div>
          <ServiceValue
            display={draft.notes || 'Add note'}
            kind="textarea"
            label="Notes"
            onChange={(notes) => onChange({ notes: notes.trim() ? notes : null })}
            value={draft.notes || ''}
          />
        </div>
      </div>
    </article>
  )
}

function ServicesEditor({ active = true, quoteId, quote, onQuoteChange, onWorkflowChange }: WorkflowTabProps) {
  const api = useQuoteBuilderApi()
  const queryClient = useQueryClient()
  const options = servicesQueryOptions(api, quoteId)
  const queryKey = options.queryKey
  const latestQuote = useRef(quote)
  latestQuote.current = quote
  const cachedPreview = queryClient.getQueryData<InstallationPreview>(queryKey)
  const baseline = useRef<InstallationEstimateInput[]>(cachedPreview?.estimates.map(draftEstimate) ?? [])
  const accepted = useRef<InstallationPreview | null>(cachedPreview ?? null)
  const [drafts, setDrafts] = useState<InstallationEstimateInput[]>(
    () => cachedPreview?.estimates.map(draftEstimate) ?? [],
  )
  const [refreshFailed, setRefreshFailed] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [notice, setNotice] = useState('')
  const [pricingError, setPricingError] = useState('')
  const [expanded, setExpanded] = useState(false)
  const preview = useQuery({ ...options, enabled: active })

  function accept(next: InstallationPreview, submitted?: InstallationEstimateInput[]) {
    const cached = queryClient.getQueryData<InstallationPreview>(queryKey)
    if (cached && cached.revision > next.revision) next = cached
    if (accepted.current && next.revision < accepted.current.revision) return
    const previous = submitted ?? baseline.current
    const incoming = next.estimates.map(draftEstimate)
    setDrafts((current) => mergeDrafts(current, previous, incoming))
    baseline.current = incoming
    accepted.current = next
    queryClient.setQueryData(queryKey, next)
  }

  async function refresh(submitted?: InstallationEstimateInput[]) {
    setRefreshing(true)
    try {
      const [nextQuote, nextPreview, nextWorkflow] = await Promise.all([
        queryClient.fetchQuery({
          ...quoteQueryOptions(api, quoteId),
          staleTime: 0,
        }),
        queryClient.fetchQuery({ ...options, staleTime: 0 }),
        queryClient.fetchQuery({
          ...workflowQueryOptions(api, quoteId),
          staleTime: 0,
        }),
      ])
      if (nextQuote.revision >= latestQuote.current.revision) onQuoteChange(nextQuote)
      if (nextWorkflow.revision >= latestQuote.current.revision) onWorkflowChange(nextWorkflow)
      accept(nextPreview, submitted)
      setRefreshFailed(false)
    } catch {
      setRefreshFailed(true)
    } finally {
      setRefreshing(false)
    }
  }

  const apply = useMutation({
    mutationFn: (submitted: InstallationEstimateInput[]) =>
      applyServices(
        api,
        {
          id: quoteId,
          revision: accepted.current?.revision ?? latestQuote.current.revision,
        },
        submitted,
        baseline.current,
      ),
    onMutate: async () => {
      setNotice('')
      setRefreshFailed(false)
      await queryClient.cancelQueries({ queryKey })
    },
    onSuccess: (result, submitted) => {
      if (result.quote.revision >= latestQuote.current.revision) {
        latestQuote.current = result.quote
        onQuoteChange(result.quote)
      }
      // Keep edits typed after submission; only the submitted values were saved.
      setDrafts((current) => mergeDrafts(current, submitted, result.submitted))
      baseline.current = result.submitted
      if (accepted.current)
        accepted.current = {
          ...accepted.current,
          revision: result.quote.revision,
        }
      setNotice('Services saved.')
      void queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false })
    },
    onError: async (error) => {
      setNotice('')
      if (error instanceof ServicesConflictError) {
        if (error.quote.revision >= latestQuote.current.revision) onQuoteChange(error.quote)
        accept(error.preview)
      } else await refresh()
    },
  })

  useEffect(() => {
    if (
      !preview.data ||
      preview.data === accepted.current ||
      apply.isPending ||
      preview.data.revision < latestQuote.current.revision
    )
      return
    accept(preview.data)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the demo accepts each newer read once
  }, [preview.data, apply.isPending])

  useEffect(() => {
    if (active && !apply.isPending && preview.data && quote.revision > preview.data.revision) {
      void queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when the quote moves past the read
  }, [active, quote.revision, apply.isPending, preview.dataUpdatedAt])

  const hasChanges = JSON.stringify(drafts) !== JSON.stringify(baseline.current)
  const invalid = drafts.some(draftIsInvalid)
  useEffect(() => {
    if (!hasChanges || invalid || apply.isPending || apply.isError || !accepted.current) return
    const timer = window.setTimeout(() => apply.mutate(drafts), 400)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- autosave follows the drafts, as in the demo
  }, [drafts, hasChanges, invalid, apply.isPending, apply.isError])

  if (preview.isLoading) return <PageState kind="loading" title="Loading installation estimate" />
  if (!preview.data) {
    return (
      <SurfaceCard>
        <PageState
          action={
            <Button onClick={() => void preview.refetch()} variant="outline">
              <RefreshCw /> Refresh services
            </Button>
          }
          description={servicesError(preview.error).replace('could not be saved', 'could not load')}
          kind="error"
          title="Installation estimate unavailable"
        />
      </SurfaceCard>
    )
  }

  const estimates = preview.data.estimates
  if (!estimates.length) {
    return (
      <SurfaceCard>
        <PageState
          description="Add equipment to estimate installation."
          kind="empty"
          title="No installation services"
        />
      </SurfaceCard>
    )
  }
  const totals = drafts.reduce(
    (sum, draft) => {
      const estimate = estimates.find((item) => item.equipment_line_id === draft.equipment_line_id)
      const pricing = servicePricing(
        draft,
        quote.lines.find((line) => line.id === estimate?.service_line_id),
      )
      return { cost: sum.cost + pricing.cost, sell: sum.sell + pricing.sell }
    },
    { cost: 0, sell: 0 },
  )
  if (!hasChanges) {
    totals.cost = Number(preview.data.total)
    totals.sell = Number(preview.data.sell_total)
  }
  const busy = apply.isPending || refreshing
  const intelligence = serviceIntelligence(drafts)
  const markup = totals.cost ? (totals.sell / totals.cost - 1) * 100 : 0
  const anyIncluded = drafts.some((row) => row.included)

  function editTotal(field: 'net' | 'markup' | 'sell', value: string) {
    const amount = Number(value)
    const nextMarkup = field === 'sell' ? (amount / totals.cost - 1) * 100 : amount
    if (
      !value.trim() ||
      !Number.isFinite(amount) ||
      (field !== 'markup' && amount < 0) ||
      (field !== 'net' && (!Number.isFinite(nextMarkup) || nextMarkup < -100 || nextMarkup > 10000))
    ) {
      setPricingError('Enter a price of zero or more, or a markup between -100% and 10,000%.')
      return
    }
    setPricingError('')
    setNotice('')
    setDrafts((current) =>
      field === 'net'
        ? allocateServiceNet(current, round(amount))
        : field === 'sell'
          ? allocateServiceSell(current, round(amount))
          : current.map((row) => (row.included ? { ...row, markup_percent: String(nextMarkup) } : row)),
    )
  }

  const rows: Array<{
    estimate: InstallationEstimate
    number: string
    child: boolean
  }> = []
  const equipment = quote.lines.filter((line) => estimates.some((estimate) => estimate.equipment_line_id === line.id))
  let parentNumber = 0
  for (const line of equipment.filter(
    (item) => !item.parent_line_id || !equipment.some((parent) => parent.id === item.parent_line_id),
  )) {
    const estimate = estimates.find((item) => item.equipment_line_id === line.id)
    if (!estimate) continue
    rows.push({ estimate, number: String(++parentNumber), child: false })
    equipment
      .filter((childLine) => childLine.parent_line_id === line.id)
      .forEach((childLine, index) => {
        const childEstimate = estimates.find((item) => item.equipment_line_id === childLine.id)
        if (childEstimate)
          rows.push({
            estimate: childEstimate,
            number: `${parentNumber}.${index + 1}`,
            child: true,
          })
      })
  }

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div>
          <h2 className="text-base font-semibold">Installation services</h2>
          <ReviewChoiceGroup
            className="mt-2"
            disabled={busy || drafts.length === 0}
            label="Installation on this quote"
            onChange={(choice) => {
              setNotice('')
              setPricingError('')
              setDrafts((current) => current.map((row) => ({ ...row, included: choice === 'install' })))
            }}
            options={INSTALL_CHOICES}
            value={drafts.length === 0 ? null : anyIncluded ? 'install' : 'no_install'}
          />
          {hasChanges ? <p className="mt-1 text-xs text-muted-foreground">Unsaved changes</p> : null}
        </div>
        <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
          <div
            aria-label="Installation pricing"
            className="grid w-full grid-cols-3 gap-3 sm:w-auto sm:grid-cols-[repeat(3,minmax(8.5rem,auto))]"
            role="group"
          >
            {(
              [
                ['net', 'Net', formatMoney(totals.cost), String(round(totals.cost)), !anyIncluded],
                ['markup', 'Markup', `${markup.toFixed(2)}%`, String(round(markup)), totals.cost <= 0],
                ['sell', 'Sell', formatMoney(totals.sell), String(round(totals.sell)), totals.cost <= 0],
              ] as const
            ).map(([field, label, display, value, unavailable]) => (
              <div className="min-w-0" key={field}>
                <span className="block text-xs text-muted-foreground">{label}</span>
                <POInlineEditableField
                  ariaLabel={`Installation ${label}`}
                  buttonClassName="-ml-2 min-w-0 w-full justify-start text-sm font-semibold tabular-nums sm:text-base"
                  disabled={busy || invalid || unavailable}
                  displayValue={display}
                  invalid={Boolean(pricingError)}
                  kind={field === 'markup' ? 'text' : 'money'}
                  onConfirm={(next) => editTotal(field, next)}
                  onEditingChange={(editing) => editing && setPricingError('')}
                  title={`Edit installation ${label.toLowerCase()}`}
                  value={value}
                />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <LoadingButton
              disabled={
                invalid || Boolean(pricingError) || (!hasChanges && estimates.every((estimate) => estimate.reviewed))
              }
              loading={apply.isPending}
              loadingText="Saving..."
              onClick={() => apply.mutate(drafts)}
            >
              <Check /> {apply.isError ? 'Retry save' : hasChanges ? 'Save now' : 'Apply decisions'}
            </LoadingButton>
            <Button disabled title="Automatic installer requests are not available yet" variant="outline">
              <Send /> Request installer quote
            </Button>
          </div>
        </div>
      </div>
      {pricingError ? (
        <p className="text-sm text-destructive" role="alert">
          {pricingError}
        </p>
      ) : null}
      {invalid ? (
        <p className="text-sm font-medium text-destructive" role="alert">
          Enter positive hours, a whole-number quantity, prices of zero or more, and a margin below 100%.
        </p>
      ) : null}
      {refreshFailed || preview.isError || apply.error ? (
        <div
          className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          role="alert"
        >
          <AlertTriangle className="size-4 shrink-0" />
          <span className="min-w-0 flex-1">
            {refreshFailed
              ? 'The latest totals could not load. Refresh services; your edits are retained.'
              : apply.error
                ? servicesError(apply.error)
                : 'Services could not refresh. Your edits are retained.'}
          </span>
          <Button disabled={refreshing || apply.isPending} onClick={() => void refresh()} size="sm" variant="outline">
            <RefreshCw className={cn(refreshing && 'animate-spin')} /> Refresh services
          </Button>
        </div>
      ) : notice && !hasChanges ? (
        <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400" role="status">
          {notice}
        </p>
      ) : null}
      {anyIncluded ? (
      <SurfaceCard>
        <div className="grid gap-3 px-4 py-3">
          <h3 className="text-sm font-semibold">Service intelligence</h3>
          <AiData className="grid grid-cols-3 gap-3 px-3 py-2" label="Equipment-based installation estimate">
            <div>
              <p className="text-xs text-muted-foreground">Total crew hours</p>
              <p className="mt-1 text-sm font-semibold tabular-nums">{round(intelligence.hours)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Weighted avg. rate</p>
              <p className="mt-1 text-sm font-semibold">
                <Money className="font-semibold" value={intelligence.rate} />
                /hr
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Estimate</p>
              <p className="mt-1 text-sm font-semibold">
                <Money className="font-semibold" value={intelligence.estimate} />
              </p>
            </div>
          </AiData>
          <button
            aria-controls="installation-calculator"
            aria-expanded={expanded}
            className="flex min-h-9 items-center justify-between gap-2 rounded-md text-left text-sm font-medium outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-ring/50"
            onClick={() => setExpanded(!expanded)}
            type="button"
          >
            <span className="flex items-center gap-2">
              <Wrench aria-hidden="true" className="size-4 text-muted-foreground" />
              Equipment calculator
            </span>
            <ChevronDown aria-hidden="true" className={cn('size-4 transition-transform', expanded && 'rotate-180')} />
          </button>
        </div>
        {expanded ? (
          <div className="border-t" id="installation-calculator">
            {rows.map(({ estimate, number, child }) => (
              <ServiceRow
                child={child}
                draft={
                  drafts.find((item) => item.equipment_line_id === estimate.equipment_line_id) ??
                  draftEstimate(estimate)
                }
                estimate={estimate}
                key={estimate.equipment_line_id}
                line={quote.lines.find((line) => line.id === estimate.equipment_line_id)}
                number={number}
                onChange={(changes) => {
                  setNotice('')
                  setDrafts((current) =>
                    current.map((draft) =>
                      draft.equipment_line_id === estimate.equipment_line_id ? { ...draft, ...changes } : draft,
                    ),
                  )
                }}
                serviceLine={quote.lines.find((line) => line.id === estimate.service_line_id)}
              />
            ))}
          </div>
        ) : null}
      </SurfaceCard>
      ) : drafts.length ? (
        <p className="text-sm text-muted-foreground">No installation on this quote.</p>
      ) : null}
    </div>
  )
}

/** The Services stage: installation labor per equipment line, priced and put on the quote. */
export function ServicesTab(props: WorkflowTabProps) {
  return <ServicesEditor key={props.quoteId} {...props} />
}
